<template>
  <!-- Inicio de sesión. HTML + CSS plano (sin v-form / v-text-field / v-dialog / v-select) para que funcione igual en Windows 7. -->
  <div class="ps-auth">
    <div class="ps-auth__card">
      <aside class="ps-auth__hero">
        <div class="ps-auth__hero-overlay"></div>
        <div class="ps-auth__hero-content">
          <div class="ps-auth__hero-title">Tu nutrición deportiva, sin vueltas.</div>
          <ul class="ps-auth__perks">
            <li><i class="mdi mdi-truck-fast-outline" aria-hidden="true"></i> Envíos a todo el país</li>
            <li><i class="mdi mdi-shield-check-outline" aria-hidden="true"></i> Pago seguro</li>
            <li><i class="mdi mdi-star-outline" aria-hidden="true"></i> Productos originales</li>
          </ul>
        </div>
      </aside>

      <section class="ps-auth__panel">
        <form class="ps-auth__form" novalidate @submit.prevent="loginUser">
          <h1 class="ps-auth__title">{{ resources.WelcomeMessage }}</h1>
          <p class="ps-auth__subtitle">{{ resources.InsertCredentials }}</p>

          <div class="ps-field" :class="{ 'ps-field--error': submitted && !username }">
            <label for="login-user">{{ resources.User }}</label>
            <input id="login-user" v-model="username" type="text" autocomplete="username" />
            <small v-if="submitted && !username" class="ps-field__error">{{ resources.RequiredField }}</small>
          </div>

          <div class="ps-field ps-field--spaced" :class="{ 'ps-field--error': submitted && !password }">
            <label for="login-password">{{ resources.Password }}</label>
            <div class="ps-field__group">
              <input id="login-password" v-model="password" :type="show ? 'text' : 'password'" autocomplete="current-password" />
              <button type="button" class="ps-field__toggle" :aria-label="show ? 'Ocultar la contraseña' : 'Mostrar la contraseña'" @click="toggleShow">
                <i class="mdi" :class="show ? 'mdi-eye-off-outline' : 'mdi-eye-outline'" aria-hidden="true"></i>
              </button>
            </div>
            <small v-if="submitted && !password" class="ps-field__error">{{ resources.RequiredField }}</small>
          </div>

          <div class="ps-auth__forgot">
            <button type="button" class="ps-auth__link" @click="resetPassword">{{ resources.ForgotPassword }}</button>
          </div>

          <button type="submit" class="ps-btn ps-btn--primary ps-btn--block ps-btn--lg" :disabled="globalStore.loading">
            <span v-if="globalStore.loading" class="ps-spinner ps-spinner--sm" aria-hidden="true"></span>
            {{ resources.Login }}
          </button>

          <div class="ps-auth__divider"><span>{{ resources.DontHaveAccount }}</span></div>

          <button type="button" class="ps-btn ps-btn--ghost ps-btn--block" @click="registerForm">Crear cuenta</button>
        </form>
      </section>
    </div>
  </div>

  <!-- Selección de sucursal -->
  <div v-if="showPopup" class="ps-modal" role="dialog" aria-modal="true">
    <div class="ps-modal__card">
      <h2 class="ps-modal__title">{{ resources.SelectBranch }}</h2>

      <div class="ps-field">
        <label for="login-branch">{{ resources.Branch }}</label>
        <select id="login-branch" v-model="idBranch">
          <option :value="null" disabled>—</option>
          <option v-for="b in branches" :key="b.value" :value="b.value">{{ b.title }}</option>
        </select>
        <small class="ps-field__hint">{{ resources.BranchHelp }}</small>
      </div>

      <div class="ps-modal__actions">
        <button type="button" class="ps-btn ps-btn--primary" :disabled="globalStore.loading || !idBranch" @click="loginUser">
          <i class="mdi mdi-login" aria-hidden="true"></i>
          {{ resources.Login }}
        </button>
      </div>
    </div>
  </div>

  <!-- Verificación en dos pasos -->
  <div v-if="showMfa" class="ps-modal" role="dialog" aria-modal="true">
    <div class="ps-modal__card">
      <h2 class="ps-modal__title">Verificación en dos pasos</h2>
      <p class="ps-modal__text">Ingresá el código de 6 dígitos de tu app de autenticación, o un código de recuperación.</p>

      <div class="ps-field">
        <label for="login-mfa">Código</label>
        <input id="login-mfa" ref="mfaInput" v-model="mfaCode" type="text" autocomplete="one-time-code" @keyup.enter="submitMfa" />
      </div>

      <div class="ps-modal__actions ps-modal__actions--split">
        <button type="button" class="ps-btn ps-btn--text" @click="showMfa = false">{{ resources.Cancel }}</button>
        <button type="button" class="ps-btn ps-btn--primary" :disabled="globalStore.loading" @click="submitMfa">{{ resources.Confirm }}</button>
      </div>
    </div>
  </div>

  <!-- Recuperar contraseña -->
  <div v-if="showPopupPassword" class="ps-modal" role="dialog" aria-modal="true">
    <form class="ps-modal__card" novalidate @submit.prevent="sendRecoveryMail">
      <h2 class="ps-modal__title">{{ resources.PasswordRecoveryTitle }}</h2>

      <div class="ps-field" :class="{ 'ps-field--error': recoveryError }">
        <label for="login-recovery">{{ resources.Email }}</label>
        <input id="login-recovery" v-model="email" type="email" autocomplete="email" />
        <small v-if="recoveryError" class="ps-field__error">{{ recoveryError }}</small>
      </div>

      <div class="ps-modal__actions ps-modal__actions--split">
        <button type="button" class="ps-btn ps-btn--text ps-btn--danger" :disabled="loadingSave" @click="showPopupPassword = false">
          {{ resources.Cancel }}
        </button>
        <button type="submit" class="ps-btn ps-btn--primary" :disabled="loadingSave">
          <i class="mdi mdi-email-arrow-left-outline" aria-hidden="true"></i>
          {{ resources.Confirm }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { storeToRefs } from "pinia";
import { nextTick, ref, watch } from "vue";

const globalStore = window.useGlobalStore();

const { resources } = storeToRefs(globalStore);

const username = ref(null);
const password = ref(null);
const submitted = ref(false);
const idBranch = ref(null);
const branches = ref([]);
const showPopup = ref(false);
const showPopupPassword = ref(false);
const email = ref(null);
const recoveryError = ref("");
const showMfa = ref(false);
const mfaCode = ref("");
const mfaInput = ref(null);
const mfaTicket = ref(null);
const loadingSave = ref(false);

watch(showMfa, (open) => {
  if (open) nextTick(() => mfaInput.value?.focus());
});

const show = ref(false);
const toggleShow = () => {
  show.value = !show.value;
};

const resetPassword = () => {
  email.value = null;
  recoveryError.value = "";
  showPopupPassword.value = true;
};

const registerForm = () => {
  window.location.href = "/Home/Register";
};

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const sendRecoveryMail = async () => {
  recoveryError.value = "";
  if (!email.value) recoveryError.value = resources.value.RequiredField;
  else if (!emailRegex.test(email.value)) recoveryError.value = resources.value.InvalidMail;

  if (recoveryError.value) return globalStore.showError(resources.value.EmailIsRequired);

  globalStore.setLoading(true);
  loadingSave.value = true;
  try {
    window.axios
      .post("/Home/RecoverPassword", { email: email.value })
      .then((res) => {
        const jsonData = res.data;
        if (jsonData.result == 0) {
          showPopupPassword.value = false;
          globalStore.showSuccess(jsonData.content);
        } else {
          globalStore.showError(jsonData.content);
        }
      })
      .catch(globalStore.errorHandler)
      .finally(() => {
        globalStore.setLoading(false);
        loadingSave.value = false;
      });
  } catch (error) {
    globalStore.setLoading(false);
    loadingSave.value = false;
    console.error(error);
    globalStore.showError(resources.value.ErrorGeneric);
  }
};

const submitMfa = () => {
  globalStore.setLoading(true);
  window.axios
    .post("/Home/LoginMfa", { ticket: mfaTicket.value, code: mfaCode.value })
    .then((res) => {
      if (res.data.result == 2) window.location.href = res.data.url;
    })
    .catch((err) => {
      // Ticket vencido o código incorrecto: si venció, hay que volver a ingresar la clave.
      globalStore.errorHandler(err);
      mfaCode.value = "";
    })
    .finally(() => {
      globalStore.setLoading(false);
    });
};

const loginUser = async () => {
  submitted.value = true;
  if (!username.value || !password.value) {
    return globalStore.showError(resources.value.FormContainsErrors);
  }

  globalStore.setLoading(true);

  try {
    window.axios
      .post("/Home/Login", {
        username: username.value,
        password: password.value,
        idBranch: idBranch.value,
      })
      .then((res) => {
        const jsonData = res.data;
        if (jsonData.result == 0 && jsonData.content && jsonData.content.mfaRequired) {
          mfaTicket.value = jsonData.content.ticket;
          mfaCode.value = "";
          showMfa.value = true;
        } else if (jsonData.result == 0) {
          showPopup.value = true;
          branches.value = Array.from(jsonData.content);
        } else if (jsonData.result == 2) {
          window.location.href = jsonData.url;
        } else {
          globalStore.showError(jsonData.error);
        }
      })
      .catch(globalStore.errorHandler)
      .finally(() => {
        globalStore.setLoading(false);
      });
  } catch (error) {
    globalStore.setLoading(false);
    console.error(error);
    globalStore.showError(resources.value.ErrorGeneric);
  }
};
</script>

<style scoped>
.ps-auth {
  min-height: calc(100vh - 140px);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 32px 16px;
  background: #e3dbce;
}

.ps-auth__card {
  display: flex;
  width: 100%;
  max-width: 960px;
  overflow: hidden;
  border-radius: 24px;
  background: #f4efe6;
  box-shadow: 0 24px 60px rgba(26, 26, 0, 0.2);
}

.ps-auth__hero {
  position: relative;
  display: flex;
  align-items: flex-end;
  flex: 1 1 45%;
  min-height: 560px;
  background-color: #124b4e;
  background-image: url("/assets/imgs/login-left.png");
  background-size: cover;
  background-position: center;
}

.ps-auth__hero-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: linear-gradient(180deg, rgba(18, 75, 78, 0.15) 0%, rgba(18, 75, 78, 0.92) 100%);
}

.ps-auth__hero-content {
  position: relative;
  z-index: 1;
  padding: 36px;
  color: #f4efe6;
}

.ps-auth__hero-title {
  margin-bottom: 18px;
  font-family: var(--ps-font-title, "Poppins", Arial, sans-serif);
  font-size: 26px;
  font-weight: 800;
  line-height: 1.2;
}

.ps-auth__perks {
  display: grid;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 14px;
}

.ps-auth__perks li {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ps-auth__perks .mdi {
  font-size: 20px;
  color: #a9d840;
}

.ps-auth__panel {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 55%;
  padding: 48px 40px;
}

.ps-auth__form {
  width: 100%;
  max-width: 380px;
}

.ps-auth__title {
  margin: 0 0 6px;
  font-family: var(--ps-font-title, "Poppins", Arial, sans-serif);
  font-size: 30px;
  font-weight: 800;
  line-height: 1.15;
  color: #124b4e;
}

.ps-auth__subtitle {
  margin: 0 0 26px;
  font-size: 14px;
  color: rgba(26, 26, 0, 0.62);
}

.ps-field--spaced {
  margin-top: 14px;
}

.ps-auth__forgot {
  display: flex;
  justify-content: flex-end;
  margin: 6px 0 20px;
}

.ps-auth__link {
  padding: 4px 0;
  border: 0;
  background: none;
  color: #124b4e;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.ps-auth__link:hover {
  text-decoration: underline;
}

.ps-auth__divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0 16px;
  font-size: 13px;
  color: rgba(26, 26, 0, 0.55);
}

.ps-auth__divider::before,
.ps-auth__divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: rgba(26, 26, 0, 0.15);
}

/* ---------- Celular: sin la imagen lateral ---------- */
@media (max-width: 767px) {
  .ps-auth__hero {
    display: none;
  }
}

@media (max-width: 599px) {
  .ps-auth {
    align-items: flex-start;
    padding: 16px 12px;
  }

  .ps-auth__card {
    border-radius: 18px;
  }

  .ps-auth__panel {
    padding: 28px 20px 32px;
  }

  .ps-auth__title {
    font-size: 26px;
  }
}
</style>
